import { Component, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AssetUriService } from '@aiwyn/config';
import { AuthService, EncryptedContactIdRouteInterceptor, LoginEmailService, SendPasswordlessLoginEmailUserError } from '@aiwyn/auth';
import { catchError, filter, finalize, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  canShowValidationErrors,
  EMAIL_REGEX,
  FormGroupControls,
  FunctionNames,
  LoadableHelper,
  LoadableStatus,
  Logger,
} from '@aiwyn/shared';
import { Store } from '@ngxs/store';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE } from '../../../routing-constants';

@Component({
  selector: 'app-login-get-help',
  templateUrl: './login-get-help.component.html',
  styleUrls: ['./login-get-help.component.scss'],
  standalone: false,
})
export class LoginGetHelpComponent {
  readonly canShowValidationErrors = canShowValidationErrors;

  resetPasswordForm = new FormGroup<FormGroupControls<{ email: string }>>({
    email: new FormControl('', [Validators.required, Validators.email]),
  });
  initializeForm$ = this.route.queryParams.pipe(
    take(1),
    map<Params, string>(({ email }) => email),
    withLatestFrom(this.authService.getAuthEmail(), this.authService.getAuthProviderUser()),
    map(([queryParamEmail, authEmail, userLoadable]) => queryParamEmail || userLoadable.data?.getProviderEmail() || authEmail),
    tap(initialEmail => this.resetPasswordForm.controls.email.setValue(initialEmail)),
  );

  isLoginEmailSending$ = new BehaviorSubject<boolean>(false);
  isResetPasswordEmailSending$ = new BehaviorSubject<boolean>(false);

  userEmailError = signal(false);

  constructor(
    public authService: AuthService,
    public assetUriService: AssetUriService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly logger: Logger,
    private readonly store: Store,
    private readonly encryptedContactId: EncryptedContactIdRouteInterceptor,
    private readonly loginEmailService: LoginEmailService,
  ) {
    this.initializeForm$.subscribe();

    this.encryptedContactId.getDecryptedContactId().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      tap(({ data: decryptedData }) => {
        if (decryptedData) {
          this.resetPasswordForm.controls.email.setValue(decryptedData.email);
          this.resetPasswordForm.controls.email.disable();
        }
      }),
    ).subscribe();
  }

  goBack(): void {
    this.authService.getUser(true).pipe(
      take(1),
      switchMap(user => this.router.navigate(user ? [ROUTE_TOKEN_MAIN_BASE, 'accountSettings'] : [ROUTE_TOKEN_LOGIN])),
    ).subscribe();
  }

  signInWithEmailLink(): void {
    this._validate('signInWithEmailLink').pipe(
      take(1),
      switchMap(isValid => {
        if (!isValid) {
          return EMPTY;
        }

        this.isLoginEmailSending$.next(true);
        return this.loginEmailService.sendPasswordlessLoginEmail(this.resetPasswordForm.controls.email.value).pipe(
          take(1),
          catchError(error => {
            if (error instanceof SendPasswordlessLoginEmailUserError) {
              this.userEmailError.set(true);
              return EMPTY;
            }

            throw error;
          }),
          finalize(() => this.isLoginEmailSending$.next(false)),
        )
      }),
    ).subscribe();
  }

  resetPassword(): void {
    this._validate('resetPassword').pipe(
      take(1),
      switchMap(isValid => {
        if (!isValid) {
          return EMPTY;
        }

        this.isResetPasswordEmailSending$.next(true);
        return this.authService.requestPasswordReset(this.resetPasswordForm.controls.email.value).pipe(
          finalize(() => this.isResetPasswordEmailSending$.next(false)),
        );
      }),
    ).subscribe();
  }

  private _validate(callingFunction: FunctionNames<LoginGetHelpComponent>): Observable<boolean> {
    this.userEmailError.set(false);
    this.resetPasswordForm.markAllAsTouched();

    return this.encryptedContactId.getDecryptedContactId().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      map(({ status, data: decryptedData }) => {
        if (status !== LoadableStatus.ERROR && decryptedData && this.resetPasswordForm.controls.email.value.match(EMAIL_REGEX)) {
          return true;
        }

        if (this.resetPasswordForm.invalid) {
          this.logger.info(`LoginGetHelpComponent : ${callingFunction} : Form is not valid`);
        }

        return this.resetPasswordForm.valid;
      }),
    );
  }
}
